/**
 * Retorno ao usuário do botão "Sincronizar" quando não há fazenda baixada (só push).
 * Sem isto, com a fila vazia o push termina na hora e nada na tela confirma que o botão funcionou.
 * Função pura: quem lê o resumo de pendências antes e depois do envio é o chamador.
 *
 * QA HML U13: decide pelo que ficou pendente DEPOIS do envio, não pelo `currentSync`. Com um
 * envio já em andamento (o salvar dispara um), o forceSync do botão espera o outro terminar e o
 * `currentSync` pode não chegar a 2 — o clique ficava sem retorno nenhum.
 */
import { pendingCount, relevantUnreadable } from './syncStatusLine'
import type { PendingSummary } from './pendingData'

export const SYNC_NOTHING_TO_SEND_TEXT =
  'Nada para enviar. Tudo o que você fez neste aparelho já foi enviado.'
export const SYNC_SENT_TEXT = 'Envio concluído.'

export type SyncResultNotice = {
  severity: 'success' | 'warning'
  text: string
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)
const readable = (s: PendingSummary | null): s is PendingSummary =>
  !!s && relevantUnreadable(s).length === 0

/**
 * `pendingBefore`: resumo lido ANTES do envio; `pendingAfter`: lido DEPOIS (null = não foi
 * possível ler). Devolve o aviso, ou null quando não dá para afirmar nada (a barra e o painel
 * mostram "Não foi possível verificar").
 */
export function resolveSyncResultNotice(
  pendingBefore: PendingSummary | null,
  pendingAfter: PendingSummary | null,
): SyncResultNotice | null {
  if (!readable(pendingAfter)) return null

  // Recusados vencem pendentes (mesma regra da barra, syncStatusLine.ts).
  const refused = pendingAfter.deadLetter
  if (refused > 0) {
    return {
      severity: 'warning',
      text: `${refused} ${plural(refused, 'registro foi recusado', 'registros foram recusados')} pelo servidor. Veja o painel de sincronização.`,
    }
  }

  const left = pendingCount(pendingAfter)
  if (left > 0) {
    return {
      severity: 'warning',
      text: `${left} ${plural(left, 'registro ainda aguarda', 'registros ainda aguardam')} envio. Seus dados continuam salvos neste aparelho e o app tenta enviar de novo sozinho.`,
    }
  }

  // Só "nada para enviar" quando a leitura de antes também mostrou zero.
  if (readable(pendingBefore) && pendingCount(pendingBefore) === 0)
    return { severity: 'success', text: SYNC_NOTHING_TO_SEND_TEXT }
  return { severity: 'success', text: SYNC_SENT_TEXT }
}
